import { inject } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import type { ResolveFn } from '@angular/router';
import { Router } from '@angular/router';
import { omit } from 'lodash';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import type { PartialDeep } from 'type-fest';
import type { FleetValidation } from '../../../../common/interfaces/validation/fleet-validation';
import { CacheService } from '../../../../common/services/cache.service';
import { PreferencesService } from '../../../../common/services/preferences.service';
import { ValidationService } from '../../../../common/services/validation.service';
import type { FleetFormResolverData } from '../../fleet.routes';
import { FLEET_DUPLICATE_OMIT_KEYS } from '../constants/fleet-duplicate-omit-keys';
import { FleetRouterStateKey } from '../enums/fleet-router-state-key';
import { FleetStorageKey } from '../enums/fleet-storage-key';
import type { FleetForm } from '../helpers/fleet-form.helper';
import { FleetFormHelper } from '../helpers/fleet-form.helper';
import type { Fleet } from '../interfaces/fleet';

export const addFleetResolver: ResolveFn<
  Observable<FleetFormResolverData & { validation: FleetValidation }>
> = () => {
  const router = inject(Router);
  const cacheService = inject(CacheService);
  const validationService = inject(ValidationService);
  const fb = inject(NonNullableFormBuilder);
  const preferencesService = inject(PreferencesService);

  const state = router.currentNavigation()?.extras?.state;
  const category = state?.[FleetRouterStateKey.CATEGORY];
  const type = state?.[FleetRouterStateKey.TYPE];
  const template: Fleet | undefined = state?.[FleetRouterStateKey.TEMPLATE];

  const form = FleetFormHelper.buildForm(fb, preferencesService);
  const baseForm = form.getRawValue();

  const initial: PartialDeep<FleetForm> = {
    ...baseForm,
    generalInformation: {
      ...baseForm.generalInformation,
      category,
      type,
    },
  };

  return validationService.getFleetValidation(category).pipe(
    map((validation) => {
      if (template) {
        const templateForm = omit(
          FleetFormHelper.fromDto(template),
          FLEET_DUPLICATE_OMIT_KEYS,
        );

        return {
          validation,
          form: templateForm,
          initial: templateForm,
          isDuplicate: true,
        };
      }

      const cacheKey = `${FleetStorageKey.ADD_FLEET_FORM}-${category}-${type}`;
      const cache = cacheService.get<PartialDeep<FleetForm>>(cacheKey, {
        keepOnNavigation: true,
      });

      return {
        validation,
        form: cache ?? initial,
        initial,
        isDuplicate: false,
      };
    }),
  );
};
